/**
 * Deal Status Transition Service
 * Enforces business rules and preconditions for deal status changes.
 * Two halves, per Sales Workflow v2.0-NL.2.0 §5:
 *
 *   INFLOW  (buying)  sourced -> under_offer -> contract_signed -> committed ->
 *                     approved_for_payment -> paid
 *   OUTFLOW (selling) listed -> deal_pending -> sold_in_transit ->
 *                     sold_delivered -> awaiting_payment -> closed
 *
 * `sold` is kept as a stage of its own rather than being renamed into one of the two
 * new ones. 153 deals already sit on it, and nothing in the database records whether
 * they were delivered - there is no delivered_at column and not one of them carries a
 * delivery address - so relabelling them would be inventing a fact. They stay put; new
 * sales flow through the explicit stages and `sold` empties out on its own.
 *
 * `sold` used to be terminal, which meant a sale entered in error could never be
 * undone. It is now reversible for seven days: sold -> listed is allowed while
 * the deal is inside sold_cancel_deadline, and refused afterwards.
 */

import { supabase } from './supabase';
import { executeTriggersForStatus } from '../services/workflowTriggers';
import { hasPermission } from './permissions';
import { User } from '../types';
import { sellerIssuesOwnInvoice as isLicensedSellerType } from './sellerType';
import { ppsrGateForPayment } from './ppsrPaymentGate';
import { settleGate } from './arPayments';
import { isSoldStatus } from './dealLifecycle';

export type DealStatus =
  | 'sourced'
  | 'under_offer'
  | 'contract_signed'
  | 'committed'
  | 'approved_for_payment'
  | 'paid'
  | 'listed'
  | 'deal_pending'
  | 'sold'
  | 'sold_in_transit'
  | 'sold_delivered'
  | 'awaiting_payment'
  | 'closed'
  | 'cancelled';

/** Which half of the pipeline a status belongs to. */
export const INFLOW_STATUSES: DealStatus[] = [
  'sourced', 'under_offer', 'contract_signed', 'committed', 'approved_for_payment', 'paid',
];
export const OUTFLOW_STATUSES: DealStatus[] = [
  'listed',
  'deal_pending',
  'sold',
  'sold_in_transit',
  'sold_delivered',
  'awaiting_payment',
  'closed',
];

export const pipelineSideOf = (status: string): 'inflow' | 'outflow' | 'other' =>
  INFLOW_STATUSES.includes(status as DealStatus)
    ? 'inflow'
    : OUTFLOW_STATUSES.includes(status as DealStatus)
    ? 'outflow'
    : 'other';

/** How long a confirmed sale can be reversed for. §5: seven days. */
export const SALE_CANCEL_WINDOW_DAYS = 7;

export interface StatusTransitionResult {
  success: boolean;
  message: string;
  warnings?: string[];
  nextAutoActions?: string[];
}

/**
 * The canonical status registry.
 *
 * Winnie's Vehicle Status Registry spec, section 0: two specs had each defined
 * their own Vehicle.status vocabulary, "because each feature area defined its own
 * status vocabulary independently rather than referencing one shared source".
 * The same thing had happened inside the codebase. Three tables described a
 * vehicle's stage: this one, the Deal Pipeline's column definitions, and Stock
 * Management's own STAGES array - and they disagreed.
 *
 *   under_offer   Offer Accepted  vs  Under Offer
 *   paid          Paid (Ready for Sale)  vs  Paid
 *   listed        Listed  vs  In Stock
 *
 * The note below on under_offer says every screen reads this map. Stock
 * Management did not, so Winnie's rename never reached it and the same car read
 * one thing on the board and another on the stock register.
 *
 * One row per status now carries the label, the colour, its place in the
 * lifecycle and the screens entitled to show it. Nothing else defines any of
 * those four things; qa/statusregistry.mjs fails the build if anything starts to.
 *
 * Colours are the Deal Pipeline's, because that board is the one everybody reads
 * and its palette is the one Winnie has signed off. The three stages the board
 * retired - listed, sold, awaiting_payment - keep the colours Stock Management
 * had for them, since it is the only screen that still shows them.
 */
export type StatusSurface =
  | 'DealPipeline'
  | 'DDReview'
  | 'PurchaseWorkspace'
  | 'SaleWorkspace'
  | 'StockManagement'
  | 'SalesAR';

export interface StatusRegistryEntry {
  /** The database value. */
  key: DealStatus;
  /** What a person reads. The only label for this status anywhere. */
  label: string;
  /** The only colour for this status anywhere. */
  color: string;
  /** Position in the canonical lifecycle, so ordering agrees across screens. */
  sequence: number;
  /** Screens entitled to display it. */
  surfaces: readonly StatusSurface[];
  /** Retired stages keep their rows and their history; nothing new lands on them. */
  retired?: boolean;
}

export const STATUS_REGISTRY: Record<DealStatus, StatusRegistryEntry> = {
  sourced: { key: 'sourced', label: 'Sourced', color: '#6b7280', sequence: 10, surfaces: ['DealPipeline'] },
  /* Winnie: the yard says the offer has been accepted, not that the car is under offer.
     Only the label changes - the database enum stays under_offer, and because every screen
     reads this registry, the pipeline column, the card, the chip and the button all agree. */
  under_offer: { key: 'under_offer', label: 'Offer Accepted', color: '#3b82f6', sequence: 20, surfaces: ['DealPipeline'] },
  contract_signed: { key: 'contract_signed', label: 'Contract Signed', color: '#0ea5e9', sequence: 30, surfaces: ['DealPipeline', 'DDReview'] },
  committed: { key: 'committed', label: 'Committed', color: '#8b5cf6', sequence: 40, surfaces: ['DealPipeline', 'DDReview', 'PurchaseWorkspace'] },
  approved_for_payment: { key: 'approved_for_payment', label: 'Approved for Payment', color: '#f59e0b', sequence: 50, surfaces: ['DealPipeline', 'PurchaseWorkspace'] },
  paid: { key: 'paid', label: 'Paid (Ready for Sale)', color: '#10b981', sequence: 60, surfaces: ['DealPipeline', 'StockManagement', 'SaleWorkspace'] },
  listed: { key: 'listed', label: 'Listed', color: '#065f46', sequence: 70, surfaces: ['StockManagement'], retired: true },
  deal_pending: { key: 'deal_pending', label: 'Deal Pending', color: '#a855f7', sequence: 80, surfaces: ['DealPipeline', 'StockManagement', 'SaleWorkspace'] },
  sold: { key: 'sold', label: 'Sold', color: '#0f766e', sequence: 90, surfaces: ['StockManagement', 'SalesAR'], retired: true },
  sold_in_transit: { key: 'sold_in_transit', label: 'Sold \u2014 In Transit', color: '#22d3ee', sequence: 100, surfaces: ['DealPipeline', 'StockManagement', 'SalesAR', 'SaleWorkspace'] },
  sold_delivered: { key: 'sold_delivered', label: 'Sold \u2014 Delivered', color: '#34d399', sequence: 110, surfaces: ['DealPipeline', 'StockManagement', 'SalesAR', 'SaleWorkspace'] },
  awaiting_payment: { key: 'awaiting_payment', label: 'Sold \u2014 Awaiting Payment', color: '#f59e0b', sequence: 120, surfaces: ['StockManagement', 'SalesAR'], retired: true },
  /* The registry spec's `settled` is this key. Its label there is "Settled & closed
     out", and the label here stays "Closed" on purpose: settlement is tracked
     separately on this platform, as `deals.settled_at`, and a deal can be settled
     while still at Sold - Delivered. Reading "Settled" off the status alone would
     assert a payment that the status does not know about. The Sales (AR) screen
     carries the settled mark, from settled_at, where it is true. */
  closed: { key: 'closed', label: 'Closed', color: '#64748b', sequence: 130, surfaces: ['DealPipeline', 'StockManagement', 'SalesAR'] },
  cancelled: { key: 'cancelled', label: 'Cancelled', color: '#ef4444', sequence: 140, surfaces: ['DealPipeline', 'StockManagement', 'SalesAR'] },
};

/** Every status, in lifecycle order. */
export const STATUS_KEYS: DealStatus[] = (Object.keys(STATUS_REGISTRY) as DealStatus[]).sort(
  (a, b) => STATUS_REGISTRY[a].sequence - STATUS_REGISTRY[b].sequence,
);

export const statusEntry = (status: string): StatusRegistryEntry | undefined =>
  STATUS_REGISTRY[status as DealStatus];

export const statusLabel = (status: string): string =>
  statusEntry(status)?.label || status;

/** The one colour for a status. Unknown keys fall back to the neutral slate. */
export const statusColor = (status: string): string =>
  statusEntry(status)?.color || '#64748b';

export const statusSequence = (status: string): number =>
  statusEntry(status)?.sequence ?? Number.MAX_SAFE_INTEGER;

/** Whether a screen is entitled to display a status at all. */
export const statusShowsOn = (status: string, surface: StatusSurface): boolean =>
  Boolean(statusEntry(status)?.surfaces.includes(surface));

/**
 * Valid status transition rules
 */
const VALID_TRANSITIONS: Record<DealStatus, DealStatus[]> = {
  sourced: ['under_offer', 'cancelled'],
  under_offer: ['contract_signed', 'committed', 'sourced', 'cancelled'],
  contract_signed: ['committed', 'under_offer', 'cancelled'],
  committed: ['approved_for_payment', 'contract_signed', 'cancelled'],
  approved_for_payment: ['paid', 'committed', 'cancelled'],
  /* `cancelled` is reachable from `paid` because of the vehicle-return path (Winnie, 18 Sep):
     a defect found after acquisition gives the car back to the seller, and she is explicit
     that this reuses the existing Cancelled action rather than inventing a status for it. */
  paid: ['deal_pending', 'closed', 'cancelled'],
  /* `listed` and `sold` lost their columns on 10 Sep — Winnie replaced them with Deal Pending
     and the two delivery stages. The rows are not touched: they keep appearing in Stock
     Management, in Sales (AR) and in the Debtor report. They are kept reachable *forwards*
     here so the 153 imported deals sitting on `sold` are not stranded, but nothing new ever
     lands on either of them. */
  listed: ['deal_pending', 'sold_in_transit', 'sold_delivered', 'closed'],
  /* A wholesale buyer has agreed and a proposal is out, but the car has not moved. */
  deal_pending: ['sold_in_transit', 'sold_delivered', 'paid', 'closed'],
  // Reversible while inside the seven-day window — see the precondition below.
  sold: ['sold_in_transit', 'sold_delivered', 'closed'],
  /* `paid` is reachable from both sold stages for the void path (Winnie, Checks_20 Sep §9
     Path B): "Vehicle.status = paid_ready_for_sale // back into available stock". The 14
     September audit flagged that her target status was not a legal move from a sold status;
     it is now, and only for that purpose - a void is the one thing that puts a sold car back
     on the lot. */
  sold_in_transit: ['sold_delivered', 'deal_pending', 'closed', 'paid'],
  sold_delivered: ['sold_in_transit', 'deal_pending', 'closed', 'paid'],
  /* Settlement is tracked by `settled_at` and the Paid column on Sales (AR) now, so nothing
     moves a deal onto `awaiting_payment` any more. Existing rows can still be closed. */
  awaiting_payment: ['closed'],
  closed: [],
  cancelled: []
};

/**
 * The tax-invoice gate on Awaiting Payment.
 *
 * Pulled out of the sold -> awaiting_payment entry because there are now three ways into
 * Awaiting Payment, not one. Leaving it attached to a single pair would have made the two
 * new stages a way around the control rather than a refinement of it.
 */
/** The seven-day window in which a confirmed sale can still be undone. §5. */
const withinCancellationWindow = async (
  dealId: string,
): Promise<{ passed: boolean; reason?: string }> => {
  const { data } = await supabase
    .from('deals')
    .select('sold_cancel_deadline, sold_at')
    .eq('id', dealId)
    .maybeSingle();

  const deadline = (data as { sold_cancel_deadline?: string } | null)?.sold_cancel_deadline;
  // Sold before the window existed. Allow it rather than trapping old rows.
  if (!deadline) return { passed: true };
  if (new Date(deadline).getTime() >= Date.now()) return { passed: true };

  return {
    passed: false,
    reason: `The seven-day cancellation window closed on ${new Date(deadline).toLocaleDateString('en-AU')}. Cancel the invoice and raise a credit instead.`,
  };
};

const taxInvoiceRaised = async (
  dealId: string,
): Promise<{ passed: boolean; reason?: string }> => {
  const { data } = await supabase
    .from('invoices')
    .select('id, status')
    .eq('deal_id', dealId)
    .neq('status', 'draft')
    .limit(1);

  return (data || []).length > 0
    ? { passed: true }
    : {
        passed: false,
        reason:
          'Issue the tax invoice first — "Awaiting Payment" is what the invoice puts the deal into.',
      };
};

/* The board can be stale. `fromStatus` arrives from the card the user dragged, not from the
   database, so a precondition that only trusts it would judge a transition against a status
   the deal left five minutes ago. This re-reads the row before answering. */
const currentStatusIs = (
  expected: DealStatus,
  reason: string
) => async (dealId: string): Promise<{ passed: boolean; reason?: string }> => {
  const { data } = await supabase
    .from('deals')
    .select('deal_status')
    .eq('id', dealId)
    .maybeSingle();

  return { passed: data?.deal_status === expected, reason };
};

/**
 * Preconditions for each status transition, keyed by `${from}_${to}`.
 */
/* Winnie's LMCT rule, written once.

   A dealer, an auction house and a tender seller issue their own tax invoice and never
   sign our Vehicle Purchase Agreement. Two stages exist only to collect that signature,
   so for these three the invoice stands in for both the agreement and the contract.

   Two preconditions and the auto-advance all have to agree about who this covers and
   what counts as the document, which is why both questions are answered here rather
   than three times over. */
const sellerIssuesOwnInvoice = async (dealId: string): Promise<boolean> => {
  const { data } = await supabase
    .from('deals')
    .select('seller_type')
    .eq('id', dealId)
    .maybeSingle()

  /* The list of who counts lives in lib/sellerType, so this and the workspace grey-out
     cannot drift apart. */
  return isLicensedSellerType(data?.seller_type)
}

const taxInvoiceOnFile = async (dealId: string): Promise<boolean> => {
  const { count } = await supabase
    .from('dd_documents')
    .select('id', { count: 'exact', head: true })
    .eq('deal_id', dealId)
    .eq('document_type', 'tax_invoice_or_abn_invoice')
    .is('deleted_at', null)

  return !!count
}

const PRECONDITIONS: Record<string, (dealId: string) => Promise<{ passed: boolean; reason?: string }>> = {
  // To under_offer: must have a Vehicle Purchase Agreement generated
  /* Winnie's LMCT rule. A dealer, an auction house and a tender seller issue a tax
     invoice and never sign our Vehicle Purchase Agreement, so their deal cannot pass through Contract
     Signed - a stage whose whole purpose is to record a signature that will never
     arrive. It goes straight to Committed once that invoice is on file.

     The authority for this rule is the server: /api/seller/sign refuses these sellers
     outright. What follows only decides what the screen offers. Two copies of a rule is
     a thing to avoid, but a screen cannot enforce and an endpoint cannot render, and of
     the two the endpoint is the one that has to be right. */
  'under_offer_committed': async (dealId: string) => {
    if (!(await sellerIssuesOwnInvoice(dealId))) {
      return {
        passed: false,
        reason:
          'A private seller signs the Vehicle Purchase Agreement, so this deal goes through Contract Signed. Only a dealer, auction or tender purchase skips it.',
      }
    }

    /* The invoice is the contract on this deal. Committing without it on file would
       leave the deal looking finished with no document behind it. */
    if (!(await taxInvoiceOnFile(dealId))) {
      return {
        passed: false,
        reason:
          'The tax invoice has not been filed yet. On a dealer purchase that invoice is the contract, so it has to be on file before the deal is committed.',
      }
    }

    return { passed: true }
  },

  'sourced_under_offer': async (dealId: string) => {
    /* A licensed seller never signs the agreement, so asking for one here would strand
       the deal at Sourced forever. Their tax invoice is the document that stands in its
       place, and it has to be on file before the deal moves — otherwise a Type 3 deal
       could walk forward carrying nothing at all. */
    if (await sellerIssuesOwnInvoice(dealId)) {
      if (await taxInvoiceOnFile(dealId)) return { passed: true }
      return {
        passed: false,
        reason:
          'The tax invoice has not been filed yet. A dealer, auction or tender purchase moves on that invoice rather than on a Vehicle Purchase Agreement.',
      }
    }

    const { data } = await supabase
      .from('deals')
      .select('bill_of_sale_sent_at')
      .eq('id', dealId)
      .maybeSingle();

    return {
      passed: !!data?.bill_of_sale_sent_at,
      reason: 'Vehicle Purchase Agreement must be generated for this deal first'
    };
  },

  // To contract_signed: must have a captured seller signature
  'under_offer_contract_signed': async (dealId: string) => {
    // A deal can have multiple bill_of_sale_documents rows (elections row +
    // signature row), so do NOT use .single() here.
    const { data } = await supabase
      .from('bill_of_sale_documents')
      .select('seller_signed_at')
      .eq('deal_id', dealId)
      .not('seller_signed_at', 'is', null)
      .limit(1);

    return {
      passed: !!(data && data.length > 0),
      reason: 'Seller must sign the Vehicle Purchase Agreement first'
    };
  },

  // To committed: DD documents must be approved
  'contract_signed_committed': async (dealId: string) => {
    const { data } = await supabase
      .from('deals')
        .select('dd_status, dd_waived_at')
      .eq('id', dealId)
      .maybeSingle();

    return {
      // A recorded waiver counts here the same way it counts at signing. It is not
      // an approval and it does not pretend to be one: dd_status stays exactly as it
      // is, and the DD Review queue still shows this deal as outstanding.
      passed: data?.dd_status === 'approved' || Boolean(data?.dd_waived_at),
      reason: 'All due diligence documents must be approved in DD Review first'
    };
  },

  // To approved_for_payment: inspection must be completed
  'committed_approved_for_payment': async (dealId: string) => {
    const { data } = await supabase
      .from('deals')
      .select('inspection_status, inspection_outcome, inspection_proceed_reason')
      .eq('id', dealId)
      .maybeSingle();

    // §2.3: it is not enough for the inspection to have happened. A poor report
    // may still be proceeded with, but only with a reason on the deal — before
    // this, a fail advanced the deal exactly like a pass.
    const d = data as any;
    /* Winnie, 11 Sep, section 3 greys Book Inspection out for a dealer, auction or
       tender purchase, so demanding one here would strand every Type 3/4/5 deal at
       Committed for good. A recorded failure still stops the deal below, whatever the
       seller type: if somebody did inspect the car and it failed, that fact does not
       stop counting because of who sold it. */
    const inspectionRequired = !(await sellerIssuesOwnInvoice(dealId));
    if (inspectionRequired && d?.inspection_status !== 'completed') {
      return { passed: false, reason: 'Vehicle inspection must be completed first' };
    }
    /*
       Winnie, 1 Sep: "Inspection Result: Failed - vehicle does not meet purchase
       criteria, deal should not proceed."

       This is the one outcome a reason cannot argue past. 'minor_issues' means a
       poor report the team has decided to proceed on, and it is allowed through
       once the reason is recorded. 'failed' means the car should not be bought,
       so there is nothing to record a reason against - the deal has to be
       cancelled or the inspection outcome revisited.
    */
    if (d.inspection_outcome === 'failed') {
      return {
        passed: false,
        reason:
          'The inspection failed — the vehicle does not meet purchase criteria. This deal cannot proceed. Cancel it, or record a different inspection outcome if the result was entered in error.',
      }
    }
    if ((d.inspection_outcome === 'minor_issues' || d.inspection_outcome === 'fail') && !String(d.inspection_proceed_reason || '').trim()) {
      return {
        passed: false,
        reason:
          'The inspection failed. Record why the deal is proceeding anyway, or cancel it — §2.3 does not allow a silent pass.',
      };
    }
    return { passed: true };
  },

  // To paid: the two-approver payment chain must actually have completed.
  //
  // This used to return { passed: true } unconditionally, which meant the entire
  // Payment Run / Payment Approval / WBC chain — and the segregation-of-duties
  // checks inside it — could be walked straight past by dragging a Kanban card.
  'approved_for_payment_paid': async (dealId: string) => {
    /* Winnie, 11 Sep, section 4: "payment release must independently verify a cleared
       PPSR check before processing", and her acceptance criterion: "without a cleared
       PPSR check recorded against that VIN". It goes first because it is
       a question about the car, not about the paperwork. This is the board's road to Paid;
       wbcService guards the two that actually move the money. Both ask the same
       question of the same record, and neither trusts the tick on the payment run. */
    const ppsr = await ppsrGateForPayment(dealId);
    if (!ppsr.passed) {
      return { passed: false, reason: ppsr.reason };
    }

    const { data } = await supabase
      .from('payment_runs')
      .select('id, status, winnie_review_complete, tuo_review_complete')
      .eq('deal_id', dealId)
      .order('created_at', { ascending: false })
      .limit(1);

    const run = data?.[0];
    if (!run) {
      return {
        passed: false,
        reason: 'No payment run exists for this deal. Initiate a payment run and complete both approvals before marking it Paid.',
      };
    }
    if (!run.winnie_review_complete) {
      return { passed: false, reason: 'First payment approval has not been completed for this deal.' };
    }
    if (!run.tuo_review_complete) {
      return { passed: false, reason: 'Second payment release has not been completed for this deal.' };
    }
    // Winnie, 1 Sep: if there is finance owing, the payout letter is not optional.
    // Money must not leave until somebody has seen and approved the letter from the
    // financier. Either answer saying Yes is enough to require it - a disagreement
    // is resolved in favour of the more cautious answer, never the cheaper one.
    const { data: payoutDeal } = await supabase
      .from('deals')
      .select('finance_payout_applicable, seller_finance_payout_applicable')
      .eq('id', dealId)
      .maybeSingle();

    if (
      /* The register saying there is finance owing is reason enough on its own, even if
         both answers on the deal said no. */
      ppsr.encumbered ||
      payoutDeal?.finance_payout_applicable === true ||
      payoutDeal?.seller_finance_payout_applicable === true
    ) {
      const { data: letters } = await supabase
        .from('dd_documents')
        .select('id, review_decision')
        .eq('deal_id', dealId)
        .in('document_type', ['PAYOUT', 'finance_payout_letter'])
        .is('deleted_at', null);

      const approved = (letters || []).some((doc: any) => doc.review_decision === 'approved');
      if (!approved) {
        return {
          passed: false,
          reason:
            'There is finance owing on this vehicle. A finance payout letter must be uploaded and approved in DD Review before this deal can be marked Paid.',
        };
      }
    }

    return { passed: true, reason: 'Both payment approvals complete' };
  },

  // To deal pending: the car must be paid for and sitting in stock
  'paid_deal_pending': currentStatusIs(
    'paid',
    'Deal must be paid before a sales proposal can be raised'
  ),

  /* Update_10 Sep: "Deal Pending" took over from "Listed" as the gate in front of a sale, and
     the two delivery routes replaced the single "Sold" stage. Both routes are gated the same
     way — a car does not leave the yard until a proposal is out on it. */
  'deal_pending_sold_in_transit': currentStatusIs(
    'deal_pending',
    'Raise the sales proposal first - Deal Pending is what puts the car in front of a buyer'
  ),
  'deal_pending_sold_delivered': currentStatusIs(
    'deal_pending',
    'Raise the sales proposal first - Deal Pending is what puts the car in front of a buyer'
  ),

  // §5.3: the tax invoice is what moves a sale to "Sold — Awaiting Payment".
  // Without one there is nothing for the AR ladder to chase. All three routes in are
  // gated the same way.
  'sold_awaiting_payment': taxInvoiceRaised,
  'sold_in_transit_awaiting_payment': taxInvoiceRaised,
  'sold_delivered_awaiting_payment': taxInvoiceRaised,

  /* A sale can be undone for seven days, and then it stands. The same window now governs
     all three sold stages: without it, a sale that fell over after the car had been marked
     In Transit or Delivered had no way back to Listed at all. That was not a rule anyone
     chose - it was the shape of a map written when there was only one sold stage. */
  'sold_listed': withinCancellationWindow,
  'sold_in_transit_listed': withinCancellationWindow,
  'sold_delivered_listed': withinCancellationWindow,
};

/**
 * Auto-actions surfaced (as string markers) when a deal reaches a status.
 */
const AUTO_ACTIONS: Record<DealStatus, () => Promise<string[]>> = {
  sourced: async () => [],
  under_offer: async () => ['INSPECTION_BOOKING_INITIATED'],
  // Document collection used to be kicked off from here, which sent the seller
  // a second email after they had already had one with the Vehicle Purchase Agreement link.
  // It now opens when they open that link, in the seller portal endpoint, which
  // is both one email instead of two and the right order: the documents come
  // before the signature, so waiting for contract_signed was always too late.
  contract_signed: async () => [],
  committed: async () => [],
  approved_for_payment: async () => ['PAYMENT_RUN_QUEUED'],
  paid: async () => ['PPSR_DISCHARGE_INITIATED', 'INVOICE_GENERATED'],
  listed: async () => [],
  deal_pending: async () => [],
  sold: async () => [],
  sold_in_transit: async () => [],
  sold_delivered: async () => [],
  awaiting_payment: async () => ['AR_LADDER_ARMED'],
  closed: async () => [],
  cancelled: async () => []
};

/**
 * Permission required to make a given transition. A transition with no entry
 * here needs no special permission beyond being signed in.
 *
 * Previously nothing checked permissions on a status change at all, so the
 * pipeline board and the workspace "Move stage" buttons were open to every
 * authenticated user, including a read-only viewer.
 */
const TRANSITION_PERMISSIONS: Record<string, string> = {
  contract_signed_committed: 'perm-review-dd',
  committed_approved_for_payment: 'perm-1st-approval',
  approved_for_payment_paid: 'perm-2nd-release',
  sourced_cancelled: 'perm-override-exceptions',
  under_offer_cancelled: 'perm-override-exceptions',
  contract_signed_cancelled: 'perm-override-exceptions',
  committed_cancelled: 'perm-override-exceptions',
  approved_for_payment_cancelled: 'perm-override-exceptions',
  // Undoing a sale moves money-adjacent state, so it is not something any
  // signed-in user should be able to do from the board.
  sold_listed: 'perm-override-exceptions',
};

export const permissionForTransition = (from: DealStatus, to: DealStatus): string | null =>
  TRANSITION_PERMISSIONS[`${from}_${to}`] ?? null;

export const canUserTransition = (user: User | null | undefined, from: DealStatus, to: DealStatus): boolean => {
  const perm = permissionForTransition(from, to);
  return perm ? hasPermission(user, perm) : true;
};

/** Transitions that destroy value and should always be confirmed first. */
export const isDestructiveTransition = (to: DealStatus, from?: DealStatus): boolean =>
  to === 'cancelled' || (from === 'sold' && to === 'listed');

/**
 * Main transition function.
 */
export const transitionDealStatus = async (
  dealId: string,
  fromStatus: DealStatus,
  toStatus: DealStatus,
  userId?: string,
  actingUser?: User | null,
  /**
   * Why. §2.3 requires a recorded reason when a deal proceeds past a failed
   * inspection, and a reversed sale should say why too. Stored on the status
   * log so it sits with the event rather than in a note somewhere.
   */
  reason?: string
): Promise<StatusTransitionResult> => {
  // Enforce the permission for this transition wherever the caller supplies the
  // acting user, so every entry point (pipeline drag, workspace buttons, any
  // future caller) is gated by the same rule rather than each screen's own.
  if (actingUser !== undefined && !canUserTransition(actingUser, fromStatus, toStatus)) {
    const perm = permissionForTransition(fromStatus, toStatus);
    return {
      success: false,
      message: `You do not have permission to move a deal to ${statusLabel(toStatus)} (requires ${perm}).`,
    };
  }
  // Winnie, 1 Sep: the payout mismatch hold. Sales and the seller gave different
  // answers about whether finance is owing, so nobody yet knows whether a payout
  // letter is needed. Nothing moves until a human resolves it. Cancelling stays
  // available - a hold should stop a deal progressing, not trap it.
  if (toStatus !== 'cancelled') {
    const { data: heldDeal } = await supabase
      .from('deals')
      .select('payout_mismatch_held_at, payout_mismatch_resolved_at')
      .eq('id', dealId)
      .maybeSingle();

    if (heldDeal?.payout_mismatch_held_at && !heldDeal.payout_mismatch_resolved_at) {
      return {
        success: false,
        message:
          'This deal is on hold. Sales and the seller gave different answers about whether there is finance owing on this vehicle. Resolve the payout mismatch before moving the deal on.',
      };
    }
  }

  // Validate transition is allowed
  if (!VALID_TRANSITIONS[fromStatus]?.includes(toStatus)) {
    return {
      success: false,
      message: `Invalid transition: ${statusLabel(fromStatus)} → ${statusLabel(toStatus)}`
    };
  }

  /*
   * Settling a sale requires the money to be in.
   *
   * Vehicle Status Registry spec, section 4: "Moving to settled should require
   * payment_status = FullyPaid — don't allow a vehicle to be marked Settled while
   * a balance is still outstanding; that would silently drop a real receivable
   * out of the chase list." Nothing stopped it, and the failure is invisible by
   * construction: the row leaves the chase list on its way out, so the evidence
   * that something was owed disappears along with the debt.
   *
   * Only a SOLD car with a sale price is gated. A vehicle taken off the books
   * without ever selling - written off, returned, closed out of stock - has no
   * receivable to protect, and gating it would trap it here with no way out.
   *
   * The arithmetic is in lib/arPayments so the table, the reminders ladder and
   * this gate cannot come to different conclusions about the same car.
   */
  if (toStatus === 'closed' && isSoldStatus(fromStatus)) {
    const { data: sale } = await supabase
      .from('deals')
      .select('sale_price, invoice_id')
      .eq('id', dealId)
      .maybeSingle();

    const salePrice = Number((sale as { sale_price?: number } | null)?.sale_price) || 0;
    const invoiceId = (sale as { invoice_id?: string | null } | null)?.invoice_id || null;

    if (salePrice > 0) {
      let received = 0;
      if (invoiceId) {
        const { data: inv } = await supabase
          .from('invoices')
          .select('payment_amount_received')
          .eq('id', invoiceId)
          .maybeSingle();
        received = Number((inv as { payment_amount_received?: number } | null)?.payment_amount_received) || 0;
      }

      const gate = settleGate(salePrice, received);
      if (!gate.ok) {
        return { success: false, message: gate.reason };
      }
    }
  }

  // Check preconditions
  const preconditionKey = `${fromStatus}_${toStatus}`;
  if (PRECONDITIONS[preconditionKey]) {
    const preconditionResult = await PRECONDITIONS[preconditionKey](dealId);
    if (!preconditionResult.passed) {
      return {
        success: false,
        message: preconditionResult.reason || 'Precondition not met'
      };
    }
  }

  try {
    const now = new Date().toISOString();

    // Stamp the lifecycle date for this stage (migration 020). Reporting needs a
    // real sale date; before this the only thing that moved was updated_at, so
    // "sold in July" could not be answered and any later edit to a deal dragged
    // its margin into the current month.
  // A sale stamps its date and its cancellation window. Only the retired 'sold'
  // stage did that, so every deal the Sale Details workspace lands on one of the
  // two live sale statuses would be a sale with no sale date - and the margin
  // scheme, the sales report and the P&L all read that date to decide which month
  // a sale belongs to. Same stamp on all three.
  const saleStamp: Record<string, string | null> = {
    sold_at: now,
    // Seven days to change your mind.
    sold_cancel_deadline: new Date(
      Date.now() + SALE_CANCEL_WINDOW_DAYS * 86_400_000
    ).toISOString(),
  };

  const stageStamp: Record<string, Record<string, string | null>> = {
    listed: { listed_at: now },
    sold: saleStamp,
    sold_in_transit: saleStamp,
    /* Winnie, 18 Sep: the PPSR-discharge settlement rule "applies only going forward from its
       go-live date", and telling an old vehicle from a new one needs a stamp for when it
       reached sold-delivered. Stamped here, and only here. */
    sold_delivered: { ...saleStamp, reached_sold_delivered_at: now },
    closed: { closed_at: now },
    paid: { paid_at: now },
  };

    /* Reversing a sale clears the sale stamps rather than leaving a sold_at on a vehicle
     that is back on the lot.

     This used to fire only on the way to 'listed', and the status map does not allow that
     move from either live sale stage - the way back is Deal Pending. So for a real
     reversal the rule could never run: a car unsold from Sold - In Transit kept its
     sold_at and its cancellation deadline, and the margin scheme, the sales report and
     the P&L all went on counting it as a sale in that month. Driven on staging on 13 Sep
     before this was changed, and the stamps were still there afterwards. */
  const SALE_STAGES = ['sold', 'sold_in_transit', 'sold_delivered'];
  const BACK_ON_THE_LOT = ['listed', 'deal_pending'];
  if (SALE_STAGES.includes(fromStatus) && BACK_ON_THE_LOT.includes(toStatus)) {
    stageStamp[toStatus] = {
      ...(stageStamp[toStatus] || {}),
      sold_at: null,
      sold_cancel_deadline: null,
      /* reached_sold_delivered_at is deliberately NOT cleared. It records that this vehicle
         has once been at sold-delivered, which stays true after a reversal, and it is what
         decides which settlement rule the vehicle is subject to. Clearing it would quietly
         move a car back under the old rule by reversing and re-selling it. */
    };
  }

    const { error: updateError } = await supabase
      .from('deals')
      .update({
        deal_status: toStatus,
        updated_at: now,
        ...(stageStamp[toStatus] || {})
      })
      .eq('id', dealId);

    if (updateError) throw updateError;

    // Log the transition (audit trail)
    await logStatusTransition(dealId, fromStatus, toStatus, userId, reason);

    // Fire status-driven workflows (non-blocking; failures are logged, not thrown)
    executeTriggersForStatus(dealId, toStatus).catch((e) =>
      console.error('workflow triggers error:', e)
    );

    // Surface any follow-up actions
    const nextAutoActions = await AUTO_ACTIONS[toStatus]();

    return {
      success: true,
      message: `Deal moved to ${statusLabel(toStatus)}`,
      nextAutoActions
    };

  } catch (error) {
    return {
      success: false,
      message: error instanceof Error ? error.message : 'Status transition failed'
    };
  }
};

/**
 * Log status transition for audit trail.
 */
const logStatusTransition = async (
  dealId: string,
  fromStatus: DealStatus,
  toStatus: DealStatus,
  userId?: string,
  reason?: string
) => {
  try {
    const { error } = await supabase
      .from('deal_status_log')
      .insert([{
        deal_id: dealId,
        from_status: fromStatus,
        to_status: toStatus,
        transitioned_by: userId ?? null,
        transitioned_at: new Date().toISOString(),
        reason: reason || null
      }]);

    if (error) console.error('Error logging status transition:', error);
  } catch (err) {
    console.error('Failed to log status transition:', err);
  }
};

/**
 * Get the valid next statuses for a given status.
 */
export const getAvailableActions = (currentStatus: DealStatus): DealStatus[] => {
  return VALID_TRANSITIONS[currentStatus] || [];
};

/**
 * Check (without mutating) whether a deal can transition.
 */
export const canTransition = async (
  dealId: string,
  fromStatus: DealStatus,
  toStatus: DealStatus
): Promise<{ canTransition: boolean; reason?: string }> => {
  if (!VALID_TRANSITIONS[fromStatus]?.includes(toStatus)) {
    return {
      canTransition: false,
      reason: `Transition not allowed: ${statusLabel(fromStatus)} → ${statusLabel(toStatus)}`
    };
  }

  const preconditionKey = `${fromStatus}_${toStatus}`;
  if (PRECONDITIONS[preconditionKey]) {
    const result = await PRECONDITIONS[preconditionKey](dealId);
    if (!result.passed) {
      return { canTransition: false, reason: result.reason };
    }
  }

  return { canTransition: true };
};

/**
 * Auto-advance a deal when its gating tasks complete.
 */
export const autoAdvanceDealIfReady = async (dealId: string): Promise<StatusTransitionResult | null> => {
  try {
    const { data: deal, error } = await supabase
      .from('deals')
      .select('deal_status, dd_status, inspection_status, inspection_outcome, inspection_proceed_reason, payout_mismatch_held_at, payout_mismatch_resolved_at')
      .eq('id', dealId)
      .maybeSingle();

    if (error) throw error;
    if (!deal) return null;

    const currentStatus = deal.deal_status as DealStatus;

    // A held deal must not be advanced quietly in the background either.
    if (deal.payout_mismatch_held_at && !deal.payout_mismatch_resolved_at) return null;

    /* Winnie, 11 Sep: a Type 3/4/5 purchase reaches Committed as soon as its tax invoice
       is filed. It walks rather than jumps — both preconditions run and both write their
       own line in the status log, so the deal's history reads the same as any other. */
    if (currentStatus === 'sourced' || currentStatus === 'under_offer') {
      if ((await sellerIssuesOwnInvoice(dealId)) && (await taxInvoiceOnFile(dealId))) {
        let at: DealStatus = currentStatus;
        if (at === 'sourced') {
          const step = await transitionDealStatus(dealId, 'sourced', 'under_offer');
          if (!step.success) return step;
          at = 'under_offer';
        }
        return await transitionDealStatus(dealId, at, 'committed');
      }
    }
    if (currentStatus === 'contract_signed' && deal.dd_status === 'approved') {
      return await transitionDealStatus(dealId, currentStatus, 'committed');
    }

    if (currentStatus === 'committed' && deal.inspection_status === 'completed') {
      // A failed inspection with no recorded reason must not be auto-advanced —
      // it needs a person to decide. The precondition would refuse it anyway;
      // checking here keeps the log free of a failed transition every time
      // something touches the deal.
      const d = deal as any;
      /* A failed inspection is never auto-advanced. It is a stop, not a warning. */
      if (d.inspection_outcome === 'failed') return null
      if ((d.inspection_outcome === 'minor_issues' || d.inspection_outcome === 'fail') && !String(d.inspection_proceed_reason || '').trim()) {
        return null;
      }
      return await transitionDealStatus(dealId, currentStatus, 'approved_for_payment');
    }

    return null;

  } catch (error) {
    console.error('Error auto-advancing deal:', error);
    return null;
  }
};
